import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  FileModel,
  ManagedHttpContext,
  ThumbnailSource,
} from '@osos/shared/types';
import { forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ConfigService } from '../../config';

// @TODO: Support upload progress.

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private get _storageBaseUrl(): string {
    return this.configService.configuration.STORAGE_BASE_URL;
  }

  private get _storageApiUrl(): string {
    return `${this._storageBaseUrl}/storage/v2/files`;
  }

  private get _httpContextWithBypassToken(): HttpContext {
    return new ManagedHttpContext({ bypassPublicMiddleware: true });
  }

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
  ) {}

  /** Uploads the provided files and returns the Observable<FileModel>
   * @param files the files to upload
   */
  uploadFiles(files: File[]): Observable<FileModel[]> {
    const formData = new FormData();

    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < files.length; i++) {
      formData.append('file', files[i], files[i].name);
    }

    return this.httpClient.post<FileModel[]>(this._storageApiUrl, formData, {
      context: this._httpContextWithBypassToken,
    });
  }

  /** Uploads the provided file and returns the Observable<FileModel>
   * @param file the file to upload
   */
  uploadFile(
    file: File,
    thumbnailSource?: ThumbnailSource,
  ): Observable<FileModel> {
    if (!thumbnailSource) {
      return this._uploadFile(file);
    }
    return this.uploadFileWithThumb(file, thumbnailSource);
  }

  removeFile(fileId: string): Observable<any> {
    return this.httpClient.delete(`${this._storageApiUrl}/${fileId}`, {
      context: this._httpContextWithBypassToken,
    });
  }

  getBlobByFileId(fileId: string): Observable<Blob> {
    return this.httpClient
      .get(`${this._storageApiUrl}/download/${fileId}`, {
        responseType: 'blob',
        context: this._httpContextWithBypassToken,
      })
      .pipe(map((res: Blob) => res));
  }

  blobToUrl(blob: Blob): Subject<any> {
    const reader = new FileReader();
    const sub = new Subject<any>();
    reader.onload = (e) => {
      sub.next(e.target.result);
    };
    reader.readAsDataURL(blob);
    return sub;
  }

  private _uploadFile(file: File): Observable<FileModel> {
    const formData = new FormData();
    formData.append(`file`, file, file.name);
    return this.httpClient
      .post<FileModel[]>(this._storageApiUrl, formData, {
        context: this._httpContextWithBypassToken,
      })
      .pipe(
        map((res) => res.shift()),
        catchError((error) => {
          // Ignore 400 errors, as they already handled the error interceptor
          if (error.status === 400) {
            return of(null);
          }

          return throwError(() => error);
        }),
      );
  }

  private uploadFileWithThumb(
    file: File,
    thumbSource: ThumbnailSource,
  ): Observable<FileModel> {
    return forkJoin({
      thumbUploadedFile: this._uploadThumbFile(file, thumbSource),
      uploadedFile: this._uploadFile(file),
    }).pipe(
      map(({ thumbUploadedFile, uploadedFile }) => {
        return {
          ...uploadedFile,
          ...(!!thumbUploadedFile && {
            thumbnail: thumbUploadedFile,
          }),
        };
      }),
      catchError(() => {
        return throwError(() => new Error('uploadError'));
      }),
    );
  }

  private _uploadThumbFile(
    file: File,
    thumbSource: ThumbnailSource,
  ): Observable<FileModel> {
    return thumbSource(file).pipe(
      switchMap((thumb: File) => {
        if (!thumb) {
          return of(null);
        }
        return this._uploadFile(thumb);
      }),
    );
  }
}
